import React, { FC, memo } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Alert,
    Grid,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';

type OfferDataTableProps = {
    tableName: string;
    tableHeaderNames: object;
    tableCellsData: any;
    onImageClick?: (imageUrl: string) => void;
};

const OfferDataTable: FC<OfferDataTableProps> = (props: OfferDataTableProps) => {
    const {
        tableName,
        tableHeaderNames,
        tableCellsData,
        onImageClick,
    } = props;

    const getTableCellItems = (cellData: any): React.JSX.Element[] => {
        const rowData: any = [];
        Object.keys(tableHeaderNames).forEach((headerName: string, index: number) => {
            const cell = cellData[headerName];

            if (headerName === 'images') {
                const images = cell || [];
                rowData.push(
                    <TableCell key={index}>
                        {images.length > 0 ? (
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                    <ImageIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: '0.875rem' }} />
                                    {images.length} image(s)
                                </Typography>
                                <Grid container spacing={0.5}>
                                    {images.map((img: any, imgIndex: number) => {
                                        const imageUrl = img?.image || img?.imageUrl || '';
                                        const thumbUrl = img?.imageThumb || imageUrl;

                                        return (
                                            <Grid item key={imgIndex}>
                                                <Box
                                                    component="img"
                                                    src={thumbUrl}
                                                    alt={`Product Image ${imgIndex + 1}`}
                                                    sx={{
                                                        width: 60,
                                                        height: 60,
                                                        objectFit: 'cover',
                                                        borderRadius: 1,
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        cursor: onImageClick ? 'pointer' : 'default',
                                                        '&:hover': onImageClick ? {
                                                            opacity: 0.8,
                                                            borderColor: 'primary.main',
                                                        } : {},
                                                    }}
                                                    onClick={() => onImageClick && onImageClick(imageUrl)}
                                                    onError={(e: any) => {
                                                        e.target.src = 'https://via.placeholder.com/60';
                                                    }}
                                                />
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    color: 'text.secondary',
                                }}
                            >
                                <ImageIcon fontSize="small" />
                                <Typography variant="caption">No image</Typography>
                            </Box>
                        )}
                    </TableCell>
                );
            } else {
                rowData.push(
                    <TableCell key={index}>
                        {cell || '-'}
                    </TableCell>
                );
            }
        });

        return rowData;
    };

    const tableHeaderItems: React.JSX.Element[] = Object.values(tableHeaderNames)?.map(
        (headerName: any, index: number) => (
            <TableCell key={index}>
                <Typography variant="subtitle2" fontWeight={600}>
                    {headerName}
                </Typography>
            </TableCell>
        )
    );

    const tableRowItems: React.JSX.Element[] = tableCellsData?.map(
        (rowData: object, index: number) => {
            return (
                <TableRow
                    key={index}
                    hover
                    sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                    }}
                >
                    {getTableCellItems(rowData)}
                </TableRow>
            );
        }
    );

    return (
        <Box className="iauto-data-table-wrapper" sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {tableName}
            </Typography>
            <TableContainer component={Paper} elevation={2}>
                <Table sx={{ minWidth: 650 }}>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: 'action.hover' }}>
                            {tableHeaderItems}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tableRowItems && tableRowItems.length > 0 ? (
                            tableRowItems
                        ) : (
                            <TableRow>
                                <TableCell colSpan={Object.keys(tableHeaderNames).length}>
                                    <Alert severity="info">No data available</Alert>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default memo(OfferDataTable);